import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown, UserPlus, Search, Check } from "lucide-react";

/**
 * Employee picker — searches the tenant's recipient directory.
 * Also allows creating a new employee inline.
 * Props: value (recipient object or null), onChange(recipient), branchId (optional filter)
 */
export default function RecipientPicker({ value, onChange, branchId, allBranches = false, testid = "recipient-picker", label = "Recipient" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newRec, setNewRec] = useState({ name: "", department: "", phone: "", email: "" });

  const load = async (query) => {
    const params = { q: query || undefined };
    if (allBranches) params.all_branches = 1;
    else if (branchId) params.branch_id = branchId;
    const { data } = await api.get("/recipients", { params });
    setItems(data);
  };

  useEffect(() => { if (open) load(q); /* eslint-disable-next-line */ }, [open, q]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/recipients", { ...newRec, branch_id: branchId });
      toast.success("Employee added");
      setAddOpen(false); setNewRec({ name:"", department:"", phone:"", email:"" });
      onChange(data);
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="overline">{label}</div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testid}-trigger`}
            className="mt-1 w-full flex items-center justify-between border border-input bg-background px-3 py-2 text-sm hover:border-primary"
          >
            <span className={value ? "" : "text-muted-foreground"}>
              {value ? (
                <span>
                  {value.name}
                  {value.department && <span className="text-muted-foreground text-xs"> · {value.department}</span>}
                </span>
              ) : "Select employee from directory…"}
            </span>
            <ChevronDown size={14} className="text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="rounded-md p-0 w-[380px]">
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search size={14} className="text-muted-foreground" />
            <Input
              data-testid={`${testid}-search`}
              placeholder="Search by name, dept, email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-md border-0 focus-visible:ring-0 h-8 text-sm"
            />
          </div>
          <div className="max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
            {items.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No employees. Add one below.</div>}
            {items.map((r) => {
              const selected = value?.id === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`${testid}-item-${r.id}`}
                  onClick={() => { onChange(r); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-primary/5 border-b border-border/50 flex items-start justify-between"
                >
                  <div>
                    <div className="text-sm font-semibold">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground mono">
                      {r.department || "—"}{r.phone && ` · ${r.phone}`}{r.email && ` · ${r.email}`}
                    </div>
                  </div>
                  {selected && <Check size={14} className="text-primary mt-1" />}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            data-testid={`${testid}-add`}
            onClick={() => setAddOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 border-t border-border text-sm hover:bg-primary/5"
          >
            <UserPlus size={14} className="text-primary" />
            <span>Add new employee</span>
          </button>
        </PopoverContent>
      </Popover>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-md max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add employee</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <F label="Full name" v={newRec.name} onChange={v => setNewRec({ ...newRec, name: v })} required t="new-emp-name" />
            <F label="Department" v={newRec.department} onChange={v => setNewRec({ ...newRec, department: v })} t="new-emp-dept" />
            <F label="Phone" v={newRec.phone} onChange={v => setNewRec({ ...newRec, phone: v })} t="new-emp-phone" />
            <F label="Email" v={newRec.email} onChange={v => setNewRec({ ...newRec, email: v })} type="email" t="new-emp-email" />
            <Button type="submit" data-testid="new-emp-submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              Save employee
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, onChange, type = "text", required, t }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Input data-testid={t} value={v || ""} onChange={e => onChange(e.target.value)} type={type} required={required}
        className="mt-1 rounded-md border-input focus-visible:ring-primary" />
    </label>
  );
}
